/* eslint-disable no-alert */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Button, Card, CardBody, CardTitle, Col, FormGroup, Input, Label,
  Modal, ModalBody, ModalFooter, ModalHeader, Row, Table,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import {
  del, get, post, put,
} from '../../helpers/api_helper';

const emptyBox = {
  box_name: '', length: '', width: '', height: '', unit_price: '', notes: '',
};

function Boxes({ t }) {
  const [boxes, setBoxes] = useState([]);
  const [editingBox, setEditingBox] = useState(null);
  const [form, setForm] = useState(emptyBox);
  const [saving, setSaving] = useState(false);

  const loadBoxes = async () => {
    try {
      setBoxes(await get('/api/inventory/boxes'));
    } catch (error) {
      toastr.error(error.response?.data?.error || error.message);
    }
  };

  useEffect(() => {
    loadBoxes();
  }, []);

  const openEditor = (box = null) => {
    setEditingBox(box || {});
    setForm(box ? {
      box_name: box.box_name,
      length: box.length,
      width: box.width,
      height: box.height,
      unit_price: box.unit_price ?? '',
      notes: box.notes ?? '',
    } : emptyBox);
  };

  const save = async () => {
    try {
      setSaving(true);
      if (editingBox.id) {
        await put(`/api/inventory/boxes/${editingBox.id}`, form);
      } else {
        await post('/api/inventory/boxes', form);
      }
      toastr.success(t('Box saved'));
      setEditingBox(null);
      await loadBoxes();
    } catch (error) {
      toastr.error(error.response?.data?.error || error.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (box) => {
    if (!window.confirm(t(`Delete box ${box.box_name}? Inventory assignments will be cleared.`))) return;

    try {
      await del(`/api/inventory/boxes/${box.id}`);
      toastr.success(t('Box deleted'));
      await loadBoxes();
    } catch (error) {
      toastr.error(error.response?.data?.error || error.message);
    }
  };

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const populateDimensionsFromName = () => {
    if (editingBox?.id) return;

    const match = form.box_name.match(/^\s*(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)(?=\s|$)/);
    if (!match) return;

    const [length, width, height] = match.slice(1, 4).map(Number);
    if ([length, width, height].some((value) => !Number.isFinite(value) || value <= 0)) return;

    setForm((current) => ({
      ...current, length, width, height,
    }));
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col lg="6">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <CardTitle className="h4 mb-0">{t('Boxes')}</CardTitle>
                  <Button color="primary" onClick={() => openEditor()}>{t('Add Box')}</Button>
                </div>
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>{t('Name')}</th>
                      <th>{t('Length')}</th>
                      <th>{t('Width')}</th>
                      <th>{t('Height')}</th>
                      <th>{t('Unit Price')}</th>
                      <th>{t('Notes')}</th>
                      <th aria-label={t('Actions')} />
                    </tr>
                  </thead>
                  <tbody>
                    {boxes.map((box) => (
                      <tr key={box.id}>
                        <td>{box.box_name}</td>
                        <td>{box.length}</td>
                        <td>{box.width}</td>
                        <td>{box.height}</td>
                        <td>{box.unit_price == null ? '—' : `$${Number(box.unit_price).toFixed(2)}`}</td>
                        <td>{box.notes || ''}</td>
                        <td className="text-nowrap text-end">
                          <Button color="secondary" size="sm" className="me-2" onClick={() => openEditor(box)}>
                            {t('Edit')}
                          </Button>
                          <Button color="danger" size="sm" onClick={() => remove(box)}>
                            {t('Delete')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal isOpen={editingBox !== null} toggle={() => setEditingBox(null)}>
        <ModalHeader toggle={() => setEditingBox(null)}>
          {editingBox?.id ? t('Edit Box') : t('Add Box')}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="box_name">{t('Name')}</Label>
            <Input id="box_name" value={form.box_name} onChange={updateField('box_name')} onBlur={populateDimensionsFromName} />
          </FormGroup>
          <Row>
            {['length', 'width', 'height'].map((field) => (
              <Col sm="4" key={field}>
                <FormGroup>
                  <Label for={`box_${field}`}>{t(field.charAt(0).toUpperCase() + field.slice(1))}</Label>
                  <Input id={`box_${field}`} type="number" min="0" step="0.01" value={form[field]} onChange={updateField(field)} />
                </FormGroup>
              </Col>
            ))}
          </Row>
          <FormGroup>
            <Label for="unit_price">{t('Unit Price')}</Label>
            <Input id="unit_price" type="number" min="0" step="0.01" value={form.unit_price} onChange={updateField('unit_price')} />
          </FormGroup>
          <FormGroup>
            <Label for="box_notes">{t('Notes')}</Label>
            <Input id="box_notes" type="textarea" value={form.notes} onChange={updateField('notes')} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditingBox(null)}>{t('Cancel')}</Button>
          <Button color="primary" disabled={saving} onClick={save}>
            {saving ? t('Saving...') : t('Save')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

Boxes.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(Boxes);
